import React, { useEffect, useMemo } from "react";
import DomainSelect from "../Select/DomainSelect";
import DomainSelect2 from "../Select/DomainSelect2";

const REQUIRE_PARENT_SENTINEL = "__CRUD_FILTER_REQUIRE_PARENT__";

/**
 * CRUD filter dropdown: Redux-backed DomainSelect or CRUD.search DomainSelect2.
 * For DomainSelect2, optional selectMembers and parentKey add server filter { parentKey: { operand1, operator: "equals" } }.
 *
 * domainSelect2SkipFetchWithoutParent: no request until parent filter has a value.
 * domainSelect2NoParentHint: optional label for a disabled hint row (omit for empty list + «Нет выбора» only).
 */
const CRUDFilterFieldSelect = ({
  field,
  searchValue,
  advancedSearchValue,
  sendFilterValues,
  handleSearchPanelChange,
}) => {
  const parentId = field.parentKey && searchValue[field.parentKey];
  const parentId2 = field?.parentKey2 && searchValue[field?.parentKey2];
  const parentId3 = field?.parentKey3 && searchValue[field?.parentKey3];

  const parentOperand1 = parentId?.operand1;
  const parentMissing =
    Boolean(field.parentKey) &&
    (parentOperand1 == null || parentOperand1 === "");

  const skipDomainSelect2Fetch =
    Boolean(
      field.useDomainSelect2 &&
        field.domainSelectCrud &&
        field.domainSelect2SkipFetchWithoutParent &&
        parentMissing
    );

  const resolvedValue = (() => {
    switch (field.filterType) {
      case "normal": {
        let v = searchValue[field.key1 || field.key];
        if (field.key2 && v) {
          v = v[field.key2];
        }
        return v?.[`operand${field.operandPosition}`] || null;
      }
      case "advanced":
        return advancedSearchValue?.[field.key] || "";
      default:
        return null;
    }
  })();

  const resolvedValueForSelect =
    skipDomainSelect2Fetch ? null : resolvedValue;

  useEffect(() => {
    if (!skipDomainSelect2Fetch) {
      return;
    }
    if (resolvedValue == null || resolvedValue === "") {
      return;
    }
    const key = field.reducerKey || field.key;
    if (sendFilterValues && key) {
      sendFilterValues(key, null);
    }
    handleSearchPanelChange(
      null,
      field.key,
      field.compareType,
      field.operandPosition,
      field.filterType,
      field.key1,
      field.key2,
      field
    );
    field.onChange && field.onChange(null);
  }, [
    skipDomainSelect2Fetch,
    resolvedValue,
    field,
    sendFilterValues,
    handleSearchPanelChange,
  ]);

  const domainSelect2Query = useMemo(() => {
    const fromField = field.domainSelectQuery || {};
    const base = {
      paging: { skip: 0 },
      sort: { name: { operator: "asc" } },
      ...fromField,
    };
    if (field.selectMembers?.length) {
      base.select = { members: field.selectMembers };
    }
    const mergedFilter = { ...(fromField.filter || {}) };
    const p1 = parentId?.operand1;
    if (field.parentKey && p1 != null && p1 !== "") {
      mergedFilter[field.parentKey] = {
        operand1: Number(p1),
        operator: "equals",
      };
    }
    return { ...base, filter: mergedFilter };
  }, [field.domainSelectQuery, field.selectMembers, field.parentKey, parentId]);

  const domainSelect2PostProcess = useMemo(() => {
    const base = field.postProcessOptions;
    const hint = field.domainSelect2NoParentHint;
    const skip = field.domainSelect2SkipFetchWithoutParent;
    const ovk = field.optionValueKey || "id";
    const olk = field.optionLabelKey || "name";
    if (!skip || !hint) {
      return base;
    }
    return (list) => {
      let out = base ? base(list) : list;
      if (parentMissing) {
        return [
          {
            [ovk]: REQUIRE_PARENT_SENTINEL,
            [olk]: hint,
            disabled: true,
          },
          ...out,
        ];
      }
      return out;
    };
  }, [
    field.postProcessOptions,
    field.domainSelect2NoParentHint,
    field.domainSelect2SkipFetchWithoutParent,
    field.optionValueKey,
    field.optionLabelKey,
    parentMissing,
  ]);

  const onChange = (val) => {
    if (val === REQUIRE_PARENT_SENTINEL) {
      return;
    }
    const key = field.reducerKey || field.key;
    if (sendFilterValues && key) {
      sendFilterValues(key, val);
    }
    handleSearchPanelChange(
      val,
      field.key,
      field.compareType,
      field.operandPosition,
      field.filterType,
      field.key1,
      field.key2,
      field
    );
    field.onChange && field.onChange(val);
  };

  const divisionFilterOptions =
    field.key === "divisionId"
      ? (opt) =>
          !searchValue["departmentId"]?.[`operand1`] ||
          opt.departmentId === searchValue["departmentId"]?.[`operand1`]
      : null;

  if (field.useDomainSelect2 && field.domainSelectCrud) {
    return (
      <DomainSelect2
        label={field.title}
        fullWidth
        margin="none"
        crud={field.domainSelectCrud}
        query={domainSelect2Query}
        skipFetch={skipDomainSelect2Fetch}
        optionLabelKey={field.optionLabelKey}
        optionValueKey={field.optionValueKey || "id"}
        normalizeOption={field.normalizeOption}
        searchable={field.searchable}
        filter={field.filter}
        postProcessOptions={domainSelect2PostProcess}
        filterOptions={field.filterOptions ?? divisionFilterOptions}
        value={resolvedValueForSelect}
        onChange={onChange}
      />
    );
  }

  return (
    <DomainSelect
      label={field.title}
      fullWidth
      margin="none"
      filterQuery={field.filterQuery}
      reducerKey={field.reducerKey}
      reducerArrayKey={field.reducerArrayKey}
      reducerAction={field.reducerAction}
      reducerCountKey={field.reducerCountKey}
      parentKey={field.parentKey}
      parentKey2={field?.parentKey2}
      parentKey3={field?.parentKey3}
      parentId={parentId}
      parentId2={parentId2}
      parentId3={parentId3}
      optionLabelKey={field.optionLabelKey}
      optionValueKey={field.optionValueKey}
      initialOptions={field.options}
      filter={field.filter}
      searchable={field.searchable}
      value={resolvedValue}
      filterOptions={field.filterOptions ?? divisionFilterOptions}
      onChange={onChange}
    />
  );
};

export default CRUDFilterFieldSelect;
